import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import useStableId from '../../../hooks/useStableId';
import FormErrorDisplay from '../form/FormErrorDisplay';
const TextAreaContainer = styled.div`
    display: flex;
    flex-direction: column;
`;
const StyledTextArea = styled.textarea`
    display: flex;
    width: 400px;
    height: 160px;
    min-height: 56px;
    align-items: flex-start;
    flex-shrink: 0;
    padding: 12px 16px;
    border-radius: 4px;
    border: 2px solid
        ${({ theme }) => theme.components.lyt_textarea.border.default};
    background: ${({ theme }) =>
        theme.components.lyt_textarea.background.default};
    color: ${({ theme }) => theme.components.lyt_textarea.text.default};
    font-family: inherit;
    font-size: 14px;
    line-height: 1.5;
    resize: none;
    transition: border-color 0.2s ease, background-color 0.2s ease;

    &::placeholder {
        color: ${({ theme }) => theme.components.lyt_textarea.text.placeholder};
    }

    &:hover:not(:disabled):not(:focus) {
        background: ${({ theme }) =>
            theme.components.lyt_textarea.background.hover};
    }

    &:focus {
        background: ${({ theme }) =>
            theme.components.lyt_textarea.background.focus};
        border: 2px solid
            ${({ theme }) => theme.components.lyt_textarea.border.focus};
        outline: none;
    }

    &:disabled {
        background: ${({ theme }) =>
            theme.components.lyt_textarea.background.disabled};
        border: 2px solid
            ${({ theme }) => theme.components.lyt_textarea.border.disabled};
        color: ${({ theme }) => theme.components.lyt_textarea.text.disabled};
        cursor: not-allowed;
        opacity: 0.6;
    }

    &.error {
        background: ${({ theme }) =>
            theme.components.lyt_textarea.background.error};
        border: 2px solid
            ${({ theme }) => theme.components.lyt_textarea.border.error};
    }
`;
const Label = styled.label`
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.components.lyt_textarea.text.default};

    &.disabled {
        color: ${({ theme }) => theme.components.lyt_textarea.text.disabled};
        opacity: 0.6;
    }
`;
const HelperText = styled.div`
    margin-top: 4px;
    font-size: 12px;
    color: ${({ theme }) => theme.components.lyt_textarea.text.placeholder};
`;

/**
 * Accessible TextArea component that follows WAI-ARIA guidelines
 *
 * @param {Object} props - Component props
 * @param {string} props.id - Unique identifier for the textarea (required for accessibility)
 * @param {string} props.name - Name attribute for the textarea
 * @param {string} props.value - Current value of the textarea
 * @param {string} props.initialValue - Initial value of the textarea
 * @param {function} props.onChange - Handler for value changes
 * @param {string} props.label - Label text for the textarea (recommended for accessibility)
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.helperText - Assistive text shown below the textarea
 * @param {string|Array<string>} props.errorText - Error message(s) for validation errors
 * @param {boolean} props.required - Whether the textarea is required
 * @param {boolean} props.disabled - Whether the textarea is disabled
 * @param {boolean} props.readOnly - Whether the textarea is read-only
 * @param {number} props.maxLength - Maximum character length
 * @param {number} props.rows - Number of visible text rows
 * @param {string} props.describedBy - ID of an element that describes this textarea
 * @param {Object} props.aria - Additional ARIA attributes
 * @param {function} props.onFocus - Handler for focus event
 * @param {function} props.onBlur - Handler for blur event
 * @param {function} props.onKeyDown - Handler for keydown event
 */
const LytTextArea = forwardRef(
    (
        {
            id = null,
            name,
            initialValue = '',
            onChange = null,
            label = '',
            placeholder = '',
            helperText = '',
            errorText = '',
            required = false,
            disabled = false,
            readOnly = false,
            maxLength = null,
            rows = 4,
            describedBy = '',
            aria = {},
            onFocus = () => {},
            onBlur = () => {},
            onKeyDown = () => {},
            ...rest
        },
        ref
    ) => {
        const [curValue, setCurValue] = useState(initialValue);
        // Use the imported useStableId hook to generate a stable ID
        const textareaId = useStableId(`textarea-${name}`, id);
        const helperTextId = helperText
            ? `${textareaId}-helper-text`
            : undefined;
        const errorTextId = errorText ? `${textareaId}-error-text` : undefined;

        /**
         * handler for textarea change
         * @param {object} event html event
         */
        const handleChange = (event) => {
            const newValue = event.target.value;
            setCurValue(newValue);
            if (onChange) {
                onChange(newValue);
            }
        };

        // Combine IDs for aria-describedby
        const ariaDescribedBy =
            [describedBy, helperTextId, errorTextId]
                .filter(Boolean)
                .join(' ') || undefined;

        // Determine if the textarea has an error state
        const hasError = !!errorText;
        return (
            <TextAreaContainer>
                {label && (
                    <Label
                        htmlFor={textareaId}
                        className={disabled ? 'disabled' : ''}>
                        {label}
                        {required && <span aria-hidden="true"> *</span>}
                    </Label>
                )}

                <StyledTextArea
                    ref={ref}
                    id={textareaId}
                    name={name}
                    value={curValue}
                    onChange={handleChange}
                    placeholder={placeholder}
                    disabled={disabled}
                    readOnly={readOnly}
                    required={required}
                    maxLength={maxLength}
                    rows={rows}
                    aria-invalid={hasError}
                    aria-describedby={ariaDescribedBy}
                    aria-required={required}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                    className={hasError ? 'error' : ''}
                    {...aria}
                    {...rest}
                />

                {helperText && !errorText && (
                    <HelperText id={helperTextId}>{helperText}</HelperText>
                )}

                {errorText && (
                    <div id={errorTextId} role="alert">
                        <FormErrorDisplay error={errorText} />
                    </div>
                )}
            </TextAreaContainer>
        );
    }
);
LytTextArea.displayName = 'LytTextArea';
LytTextArea.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    initialValue: PropTypes.string,
    onChange: PropTypes.func,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    helperText: PropTypes.string,
    errorText: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
    ]),
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    maxLength: PropTypes.number,
    rows: PropTypes.number,
    describedBy: PropTypes.string,
    aria: PropTypes.object,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onKeyDown: PropTypes.func,
};
export default LytTextArea;
